import { useMemo, useState, type ReactElement } from 'react';
import {
  useDisciplineHours,
  shortenWeekLabel,
  type DisciplineHoursBucket,
  type DisciplineHoursPeriod,
} from '@/lib/dashboard';
import {
  DISCIPLINE_COLORS,
  DISCIPLINE_ORDER,
  type DisciplineKey,
} from '@/lib/disciplineColors';

/**
 * Gráfica apilada vertical "Horas por disciplina" (M2.16).
 *
 * Vista central del dashboard. Permite ver volumen histórico por
 * disciplina con dos granularidades (Semana / Mes) y dist. de buckets
 * configurable. SVG inline puro (sin libs) y tooltip propio con breakdown.
 *
 * Decisiones de UX:
 *  - Toggle Semana/Mes mediante segmented control accesible (botones con
 *    `aria-pressed`).
 *  - Buckets default sensatos por period (12 semanas / 6 meses), con chips
 *    para cambiar (4/12/26/52 sem, 6/12 m). Al cambiar period se resetean
 *    los buckets al default del nuevo period.
 *  - Tooltip on hover/focus en cada barra: bucket label + total horas +
 *    breakdown por disciplina (min y %). Posicionado encima de la barra
 *    con clip-to-svg para no salir del card.
 *  - Línea horizontal punteada del target del plan si llega
 *    (`targetMin > 0`), label "Target plan" a la derecha.
 *  - Loading skeleton (3 placeholders) + mensaje de error si la query
 *    falla. Empty state si todos los buckets están a 0 (perfil nuevo).
 */

const WEEK_BUCKET_OPTIONS = [4, 12, 26, 52] as const;
const MONTH_BUCKET_OPTIONS = [6, 12] as const;

const DEFAULT_BUCKETS: Record<DisciplineHoursPeriod, number> = {
  week: 12,
  month: 6,
};

export interface DisciplineHoursChartProps {
  /** Periodo inicial (default: 'week'). */
  initialPeriod?: DisciplineHoursPeriod;
}

export function DisciplineHoursChart({
  initialPeriod = 'week',
}: DisciplineHoursChartProps = {}): ReactElement {
  const [period, setPeriod] = useState<DisciplineHoursPeriod>(initialPeriod);
  const [buckets, setBuckets] = useState<number>(DEFAULT_BUCKETS[initialPeriod]);

  function changePeriod(newPeriod: DisciplineHoursPeriod) {
    if (newPeriod === period) return;
    setPeriod(newPeriod);
    setBuckets(DEFAULT_BUCKETS[newPeriod]);
  }

  const query = useDisciplineHours(period, buckets);

  return (
    <article
      aria-label="Horas por disciplina"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Horas por disciplina
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodToggle period={period} onChange={changePeriod} />
          <BucketsSelector period={period} value={buckets} onChange={setBuckets} />
        </div>
      </header>

      {query.isLoading && <ChartSkeleton />}
      {query.isError && (
        <p
          role="alert"
          data-testid="discipline-hours-error"
          className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          No se pudo cargar el desglose por disciplina.
        </p>
      )}
      {query.data && <ChartBody buckets={query.data.buckets} period={period} />}

      <Legend />
    </article>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

function PeriodToggle({
  period,
  onChange,
}: {
  period: DisciplineHoursPeriod;
  onChange: (p: DisciplineHoursPeriod) => void;
}): ReactElement {
  return (
    <div
      role="group"
      aria-label="Periodo del gráfico"
      data-testid="discipline-hours-period-toggle"
      className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs font-medium"
    >
      <button
        type="button"
        onClick={() => onChange('week')}
        aria-pressed={period === 'week'}
        className={`px-3 py-1 transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
          period === 'week'
            ? 'bg-teal-600 text-white'
            : 'bg-white text-slate-700 hover:bg-slate-50'
        }`}
      >
        Semana
      </button>
      <button
        type="button"
        onClick={() => onChange('month')}
        aria-pressed={period === 'month'}
        className={`border-l border-slate-300 px-3 py-1 transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
          period === 'month'
            ? 'bg-teal-600 text-white'
            : 'bg-white text-slate-700 hover:bg-slate-50'
        }`}
      >
        Mes
      </button>
    </div>
  );
}

function BucketsSelector({
  period,
  value,
  onChange,
}: {
  period: DisciplineHoursPeriod;
  value: number;
  onChange: (n: number) => void;
}): ReactElement {
  const options = period === 'week' ? WEEK_BUCKET_OPTIONS : MONTH_BUCKET_OPTIONS;
  return (
    <div
      role="group"
      aria-label="Número de buckets"
      data-testid="discipline-hours-buckets-selector"
      className="flex items-center gap-1 text-xs"
    >
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          aria-pressed={value === opt}
          className={`rounded-md border px-2 py-1 transition focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${
            value === opt
              ? 'border-teal-600 bg-teal-50 text-teal-800'
              : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart body
// ---------------------------------------------------------------------------

function ChartBody({
  buckets,
  period,
}: {
  buckets: DisciplineHoursBucket[];
  period: DisciplineHoursPeriod;
}): ReactElement {
  const hasData = useMemo(() => buckets.some((b) => b.totalMin > 0), [buckets]);

  if (buckets.length === 0 || !hasData) {
    return (
      <p
        data-testid="discipline-hours-empty"
        className="mt-4 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-600"
      >
        Aún no hay actividades en este rango. Sincroniza con Strava o registra un entreno.
      </p>
    );
  }

  return <ChartSvg buckets={buckets} period={period} />;
}

function ChartSkeleton(): ReactElement {
  return (
    <div
      data-testid="discipline-hours-skeleton"
      role="status"
      aria-live="polite"
      aria-label="Cargando datos del gráfico"
      className="mt-4 h-48 animate-pulse rounded-md bg-slate-100"
    />
  );
}

// ---------------------------------------------------------------------------
// SVG
// ---------------------------------------------------------------------------

const WIDTH = 800;
const HEIGHT = 280;
// `PADDING.bottom` se eleva cuando las etiquetas van rotadas (más buckets) para
// reservar espacio vertical sin que las labels rotadas -30° se solapen con los
// bordes del card. M2.17 sube el valor a 64 (de 56) en modo rotado.
const PADDING_BASE = { top: 16, right: 60, bottom: 36, left: 40 };
const PADDING_ROTATED_BOTTOM = 64;

function ChartSvg({
  buckets,
  period,
}: {
  buckets: DisciplineHoursBucket[];
  period: DisciplineHoursPeriod;
}): ReactElement {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const n = buckets.length;
  // ¿Etiquetas X rotadas? Cuando hay muchos buckets, mejor 30° para que no
  // se solapen visualmente.
  // M2.18: rotamos a partir de 8 buckets (antes 12). Con barras estrechas
  // los labels horizontales se solapaban hasta con 12 semanas porque el
  // formato corto "W19 19/5" sigue siendo de ~7 caracteres + tracking.
  const rotateLabels = n > 8;
  // Padding dinámico: subimos bottom a 64 si las labels van rotadas (M2.17).
  const PADDING = {
    ...PADDING_BASE,
    bottom: rotateLabels ? PADDING_ROTATED_BOTTOM : PADDING_BASE.bottom,
  };

  const innerW = WIDTH - PADDING.left - PADDING.right;
  const innerH = HEIGHT - PADDING.top - PADDING.bottom;

  // Eje Y: max(totalMin, targetMin) redondeado al múltiplo de 2h superior.
  const maxMin = buckets.reduce((m, b) => {
    const t = Math.max(b.totalMin, b.targetMin ?? 0);
    return Math.max(m, t);
  }, 0);
  const maxHours = Math.max(2, Math.ceil(maxMin / 60 / 2) * 2);
  const yAt = (mins: number): number => {
    const norm = mins / 60 / maxHours;
    return PADDING.top + innerH * (1 - norm);
  };

  const barGap = 4;
  const barW = Math.max(2, (innerW - barGap * Math.max(0, n - 1)) / Math.max(1, n));
  const xAt = (i: number): number => PADDING.left + i * (barW + barGap);

  // Ticks Y: 0, 25%, 50%, 75%, 100%.
  const yTicks = useMemo(() => {
    const step = maxHours / 4;
    return [0, step, step * 2, step * 3, step * 4];
  }, [maxHours]);

  // Target line: si la mayoría de buckets tiene `targetMin` definido,
  // dibujamos la línea al primer target encontrado (el plan no cambia
  // bucket a bucket en M2.16). Si no, no se dibuja.
  const targetMin = buckets.find((b) => b.targetMin !== null && b.targetMin > 0)?.targetMin ?? null;

  // Salto de labels según densidad (M2.17):
  //   1..16 buckets → mostrar todas.
  //   17..26       → cada 2.
  //   27+          → cada 4.
  // El último bucket siempre se muestra para anclar el extremo derecho.
  const labelStep = n > 26 ? 4 : n > 16 ? 2 : 1;
  function shouldShowLabel(i: number): boolean {
    if (labelStep <= 1) return true;
    if (i === n - 1) return true;
    return i % labelStep === 0;
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="relative overflow-x-auto">
        <svg
          role="img"
          aria-label={`Horas por disciplina, ${n} ${period === 'week' ? 'semanas' : 'meses'}`}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          width="100%"
          className="max-w-full"
          preserveAspectRatio="none"
          data-testid="discipline-hours-svg"
        >
          {/* Grid horizontal + ticks Y */}
          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={yAt(t * 60)}
                y2={yAt(t * 60)}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
              <text
                x={PADDING.left - 6}
                y={yAt(t * 60) + 3}
                fontSize={10}
                textAnchor="end"
                fill="#94a3b8"
              >
                {Math.round(t)}h
              </text>
            </g>
          ))}

          {/* Línea Target plan */}
          {targetMin !== null && (
            <g data-testid="discipline-hours-target-line">
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={yAt(targetMin)}
                y2={yAt(targetMin)}
                stroke="#94a3b8"
                strokeWidth={1.5}
                strokeDasharray="5,3"
              />
              <text
                x={WIDTH - PADDING.right + 4}
                y={yAt(targetMin) + 3}
                fontSize={9}
                fill="#64748b"
              >
                Target plan
              </text>
            </g>
          )}

          {/* Stacked bars */}
          {buckets.map((bucket, i) => (
            <StackedBar
              key={bucket.bucketStartISO}
              bucket={bucket}
              idx={i}
              x={xAt(i)}
              width={barW}
              yAt={yAt}
              chartBottomY={HEIGHT - PADDING.bottom}
              onHover={setHoverIdx}
            />
          ))}

          {/* Eje X labels */}
          {buckets.map((bucket, i) => {
            if (!shouldShowLabel(i)) return null;
            const labelX = xAt(i) + barW / 2;
            const labelY = HEIGHT - PADDING.bottom + 14;
            // En week-mode acortamos el label largo `W14 30 mar-5 abr` →
            // `W14 30/3` para reducir solapamiento. Mensuales se dejan tal cual.
            const display =
              period === 'week' ? shortenWeekLabel(bucket.label) : bucket.label;
            if (rotateLabels) {
              return (
                <text
                  key={`label-${bucket.bucketStartISO}`}
                  x={labelX}
                  y={labelY}
                  fontSize={9}
                  fill="#64748b"
                  textAnchor="end"
                  transform={`rotate(-30 ${labelX} ${labelY})`}
                  data-testid={`discipline-hours-label-${i}`}
                >
                  {display}
                </text>
              );
            }
            return (
              <text
                key={`label-${bucket.bucketStartISO}`}
                x={labelX}
                y={labelY}
                fontSize={10}
                fill="#64748b"
                textAnchor="middle"
                data-testid={`discipline-hours-label-${i}`}
              >
                {display}
              </text>
            );
          })}
        </svg>

        {/* Tooltip flotante */}
        {hoverIdx !== null && buckets[hoverIdx] && (
          <BucketTooltip bucket={buckets[hoverIdx]!} />
        )}
      </div>
    </div>
  );
}

// Sub-componente para la pila de una columna; expone hover por bucket entero
// además de los segmentos individuales para que el tooltip aparezca aunque
// el usuario pase el ratón por encima de un hueco entre disciplinas.
function StackedBar({
  bucket,
  idx,
  x,
  width,
  yAt,
  chartBottomY,
  onHover,
}: {
  bucket: DisciplineHoursBucket;
  idx: number;
  x: number;
  width: number;
  yAt: (mins: number) => number;
  chartBottomY: number;
  onHover: (idx: number | null) => void;
}): ReactElement {
  // Construimos segmentos apilados desde abajo (orden canónico de DISCIPLINE_ORDER).
  let cursorMin = 0;
  const segments: Array<{
    discipline: DisciplineKey;
    yTop: number;
    yBottom: number;
    height: number;
  }> = [];
  for (const d of DISCIPLINE_ORDER) {
    const min = bucket.byDiscipline[d] ?? 0;
    if (min <= 0) continue;
    const yBottom = yAt(cursorMin);
    const yTop = yAt(cursorMin + min);
    segments.push({
      discipline: d,
      yTop,
      yBottom,
      height: Math.max(0.5, yBottom - yTop),
    });
    cursorMin += min;
  }

  const stackTopY = bucket.totalMin > 0 ? yAt(bucket.totalMin) : chartBottomY;
  const stackHeight = Math.max(1, chartBottomY - stackTopY);

  return (
    <g
      data-testid={`discipline-hours-bar-${idx}`}
      onMouseEnter={() => onHover(idx)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(idx)}
      onBlur={() => onHover(null)}
      tabIndex={0}
      role="button"
      aria-label={`${bucket.label}: ${formatMinutes(bucket.totalMin)}`}
    >
      {/* Hit area completa, transparente, para captar hover incluso si la
          pila es muy baja. */}
      <rect
        x={x}
        y={stackTopY}
        width={width}
        height={stackHeight}
        fill="transparent"
      />
      {segments.map((seg) => (
        <rect
          key={seg.discipline}
          x={x}
          y={seg.yTop}
          width={width}
          height={seg.height}
          fill={DISCIPLINE_COLORS[seg.discipline].hex}
          stroke="#ffffff"
          strokeWidth={0.5}
          data-testid={`discipline-hours-bar-${idx}-${seg.discipline}`}
        />
      ))}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

function BucketTooltip({ bucket }: { bucket: DisciplineHoursBucket }): ReactElement {
  const total = bucket.totalMin;
  const rows = DISCIPLINE_ORDER.filter((d) => (bucket.byDiscipline[d] ?? 0) > 0).map((d) => {
    const mins = bucket.byDiscipline[d] ?? 0;
    const pct = total > 0 ? Math.round((mins / total) * 100) : 0;
    return { discipline: d, mins, pct };
  });
  return (
    <div
      role="tooltip"
      data-testid="discipline-hours-tooltip"
      className="pointer-events-none absolute right-2 top-2 z-10 min-w-[12rem] rounded-md border border-slate-200 bg-white p-3 text-xs shadow-lg"
    >
      <p className="font-semibold text-slate-900">{bucket.label}</p>
      <p className="mt-0.5 text-slate-700">
        Total: <span className="font-medium tabular-nums">{formatMinutes(total)}</span>
      </p>
      {bucket.targetMin !== null && bucket.targetMin > 0 && (
        <p className="text-slate-500">
          Target: <span className="tabular-nums">{formatMinutes(bucket.targetMin)}</span>
        </p>
      )}
      {rows.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {rows.map((row) => (
            <li
              key={row.discipline}
              className="flex items-center justify-between gap-3"
            >
              <span className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="inline-block h-2 w-2 rounded-sm"
                  style={{ background: DISCIPLINE_COLORS[row.discipline].hex }}
                />
                <span className="text-slate-700">
                  {DISCIPLINE_COLORS[row.discipline].label}
                </span>
              </span>
              <span className="tabular-nums text-slate-900">
                {formatMinutes(row.mins)} · {row.pct}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend(): ReactElement {
  return (
    <ul
      data-testid="discipline-hours-legend"
      className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600"
    >
      {DISCIPLINE_ORDER.map((d) => (
        <li key={d} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="inline-block h-2 w-3 rounded-sm"
            style={{ background: DISCIPLINE_COLORS[d].hex }}
          />
          <span className="font-medium text-slate-700">
            {DISCIPLINE_COLORS[d].label}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatMinutes(mins: number): string {
  if (mins <= 0) return '0h';
  const hours = mins / 60;
  if (hours >= 10) return `${Math.round(hours)}h`;
  if (hours >= 1) return `${hours.toFixed(1)}h`;
  return `${Math.round(mins)}min`;
}
